import { FC, Fragment } from 'react'
import Box from '@mui/material/Box'
import { keyframes } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'

const wordRise = keyframes`
  from { opacity: 0; transform: translateY(100%); filter: blur(var(--reveal-blur)); }
  to { opacity: 1; transform: none; filter: blur(0); }
`

const wordFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const STAGGER_MS = 45
/** Plafond du décalage cumulé : une longue phrase ne doit pas traîner. */
const MAX_STAGGER_MS = 360

export interface NfTextRevealProps {
  text: string
  /** Secondes avant le premier mot. */
  delay?: number
  /** Flou de départ, plus doux pour un texte courant que pour un titre. */
  blur?: number
}

/**
 * Révélation mot à mot, d'après le TextReveal d'uiarc : chaque mot sort de sa
 * propre découpe en montant et passe du flou au net. Joué au montage, en CSS
 * seul ; changer la `key` le rejoue. À poser dans le Typography qui porte le
 * style : seuls les mots sont rendus, plus une copie lisible par les lecteurs
 * d'écran.
 */
export const NfTextReveal: FC<NfTextRevealProps> = ({ text, delay = 0, blur = 6 }) => {
  const words = text.split(/\s+/).filter(Boolean)
  const step = words.length > 1 ? Math.min(STAGGER_MS, MAX_STAGGER_MS / (words.length - 1)) : 0

  return (
    <>
      <Box component="span" sx={visuallyHidden}>
        {text}
      </Box>
      <Box component="span" aria-hidden sx={{ '--reveal-blur': `${blur}px` }}>
        {words.map((word, index) => (
          <Fragment key={index}>
            {index > 0 && ' '}
            {/* La découpe laisse un peu de marge sous la ligne pour les jambages. */}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                overflow: 'hidden',
                verticalAlign: 'top',
                pb: '0.12em',
                mb: '-0.12em',
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  animation: `${wordRise} 560ms cubic-bezier(.2, .8, .2, 1) ${delay * 1000 + index * step}ms backwards`,
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: `${wordFade} 160ms ease ${delay * 1000}ms backwards`,
                  },
                }}
              >
                {word}
              </Box>
            </Box>
          </Fragment>
        ))}
      </Box>
    </>
  )
}

export default NfTextReveal
